/**
 * Nonce Manager - Ethereum-aligned nonce lifecycle management
 * Follows Reserve/Commit/Release pattern from NONCE_LIFECYCLE.md
 * 
 * Implements:
 * - Reserve/Commit/Release pattern (nonces reserved until finalization)
 * - Gap-tolerant validation (allows out-of-order nonce submission)
 * - Atomic operations (thread-safe nonce state transitions)
 * - On-chain nonces (synced with core consensus)
 * - Off-chain nonces (TTL-based, 24h expiration)
 * 
 * Based on: standards/docs/implementations/NONCE_LIFECYCLE.md
 */

import { InfoClient } from '../api/info/InfoClient';

export interface NonceState {
  confirmed: number;      // Confirmed nonce from core (source of truth)
  pending: number[];       // Sorted list of pending nonces (gaps allowed)
  lastSync: number;        // Last sync timestamp (milliseconds)
  mu: boolean;             // Mutex flag (simple lock for async operations)
}

export interface NonceQueryResult {
  next_nonce: number;
  reserved: boolean;
  timestamp: number;
}

export type NonceQueryTag = 'latest' | 'pending';

const SYNC_INTERVAL = 60000; // 1 minute
const MAX_PENDING_PER_ACCOUNT = 100;
const OFF_CHAIN_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const BASE_NONCE = 1; // Default starting nonce for new accounts

/**
 * NonceManager - Manages nonce lifecycle following Ethereum-aligned pattern
 */
export class NonceManager {
  private infoClient: InfoClient;
  private onChainState: Map<string, NonceState> = new Map();
  private offChainNonces: Map<string, Map<number, number>> = new Map(); // owner -> nonce -> timestamp
  private syncTimers: Map<string, NodeJS.Timeout> = new Map();

  constructor(infoClient: InfoClient) {
    this.infoClient = infoClient;
  }

  /**
   * Get or create nonce state for an owner
   */
  private getOrCreateState(owner: string): NonceState {
    const normalizedOwner = owner.toLowerCase();
    let state = this.onChainState.get(normalizedOwner);
    
    if (!state) {
      state = {
        confirmed: 0,
        pending: [],
        lastSync: 0,
        mu: false,
      };
      this.onChainState.set(normalizedOwner, state);
    }
    
    return state;
  }

  /**
   * Acquire lock for nonce state (simple async mutex)
   */
  private async acquireLock(state: NonceState): Promise<void> {
    while (state.mu) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    state.mu = true;
  }

  /**
   * Release lock for nonce state
   */
  private releaseLock(state: NonceState): void {
    state.mu = false;
  }

  /**
   * Sync with core consensus (for on-chain nonces)
   * Called automatically on GetNonce if sync interval elapsed
   */
  private async syncWithCore(owner: string): Promise<void> {
    const state = this.getOrCreateState(owner);
    const now = Date.now();
    
    // Check if sync is needed
    if (now - state.lastSync < SYNC_INTERVAL) {
      return; // Still fresh, no sync needed
    }

    try {
      // Query core for confirmed nonce
      const response = await this.infoClient.getNonceStatus(owner, false);
      const coreNonce = response?.next_nonce !== undefined
        ? (typeof response.next_nonce === 'string' 
            ? parseInt(response.next_nonce, 10) 
            : response.next_nonce)
        : 0;

      // Update confirmed nonce (one-way sync: Core → Router)
      // Note: For off-chain operations, GetNonceStatus returns on-chain nonce only
      state.confirmed = coreNonce;
      state.lastSync = now;
    } catch (error) {
      // Graceful degradation: continue with local state if core unavailable
      console.warn(`[NonceManager] Failed to sync with core for ${owner}:`, error);
    }
  }

  /**
   * Get nonce for an owner (mirrors Ethereum's eth_getTransactionCount)
   * 
   * @param owner - Wallet address
   * @param tag - Query tag ('latest' for confirmed, 'pending' for confirmed + pending count)
   * @returns Nonce value
   */
  async getNonce(owner: string, tag: NonceQueryTag = 'latest'): Promise<number> {
    const normalizedOwner = owner.toLowerCase();
    const state = this.getOrCreateState(normalizedOwner);

    // Sync with core if needed (for on-chain nonces)
    await this.syncWithCore(normalizedOwner);

    if (tag === 'latest') {
      // Return confirmed nonce only
      return state.confirmed;
    } else {
      // Return confirmed + pending count (allows gap estimation)
      const maxPending = state.pending.length > 0 
        ? Math.max(...state.pending) 
        : state.confirmed;
      return Math.max(state.confirmed, maxPending);
    }
  }

  /**
   * Validate and reserve nonce (gap-tolerant)
   * Adds nonce to pending list if valid
   * 
   * @param owner - Wallet address
   * @param nonce - Nonce value to validate
   * @param isOffChain - Whether this is an off-chain operation
   * @throws Error if nonce is invalid
   */
  async validateNonce(owner: string, nonce: number, isOffChain: boolean = false): Promise<void> {
    const normalizedOwner = owner.toLowerCase();
    const state = this.getOrCreateState(normalizedOwner);

    await this.acquireLock(state);
    
    try {
      // Sync with core if needed (for on-chain)
      if (!isOffChain) {
        await this.syncWithCore(normalizedOwner);
      }

      // Validation rules
      // 1. New account: nonce must be >= baseNonce
      if (state.confirmed === 0 && nonce < BASE_NONCE) {
        throw new Error(`nonce too low for new account: got ${nonce}, minimum ${BASE_NONCE}`);
      }

      // 2. Existing account: nonce must be > confirmed
      if (state.confirmed > 0 && nonce <= state.confirmed) {
        throw new Error(`nonce too low: got ${nonce}, confirmed ${state.confirmed}`);
      }

      // 3. Duplicate check: nonce must not already be pending
      if (state.pending.includes(nonce)) {
        throw new Error(`nonce already pending: ${nonce}`);
      }

      // 4. Memory limit: max 100 pending nonces per account
      if (state.pending.length >= MAX_PENDING_PER_ACCOUNT) {
        throw new Error(`pending nonce limit reached: max ${MAX_PENDING_PER_ACCOUNT} pending nonces`);
      }

      // 5. For off-chain: check TTL expiration
      if (isOffChain) {
        const offChainMap = this.offChainNonces.get(normalizedOwner);
        if (offChainMap) {
          const usedTimestamp = offChainMap.get(nonce);
          if (usedTimestamp) {
            const now = Date.now();
            if (now - usedTimestamp < OFF_CHAIN_TTL) {
              throw new Error(`nonce already used (off-chain): ${nonce}, expires in ${Math.floor((OFF_CHAIN_TTL - (now - usedTimestamp)) / 1000)}s`);
            }
            // TTL expired, remove from map
            offChainMap.delete(nonce);
          }
        }
      }

      // Add to pending list (maintain sorted order for efficient gap-filling)
      state.pending.push(nonce);
      state.pending.sort((a, b) => a - b);
    } finally {
      this.releaseLock(state);
    }
  }

  /**
   * Commit nonce after successful consensus execution
   * Removes from pending and increments confirmed if next-in-sequence
   * 
   * @param owner - Wallet address
   * @param nonce - Nonce value to commit
   * @param isOffChain - Whether this is an off-chain operation
   */
  async commitNonce(owner: string, nonce: number, isOffChain: boolean = false): Promise<void> {
    const normalizedOwner = owner.toLowerCase();
    const state = this.getOrCreateState(normalizedOwner);

    await this.acquireLock(state);
    
    try {
      // Remove from pending list
      const index = state.pending.indexOf(nonce);
      if (index !== -1) {
        state.pending.splice(index, 1);
      }

      if (isOffChain) {
        // For off-chain: mark as used with timestamp (TTL tracking)
        let offChainMap = this.offChainNonces.get(normalizedOwner);
        if (!offChainMap) {
          offChainMap = new Map();
          this.offChainNonces.set(normalizedOwner, offChainMap);
        }
        offChainMap.set(nonce, Date.now());
        
        // Note: Off-chain nonces don't affect confirmed nonce
        // They're tracked separately for replay protection (24h TTL)
      } else {
        // For on-chain: increment confirmed if next-in-sequence
        if (nonce === state.confirmed + 1) {
          state.confirmed = nonce;
          
          // Consume sequential gaps from pending
          while (state.pending.length > 0 && state.pending[0] === state.confirmed + 1) {
            const nextNonce = state.pending.shift()!;
            state.confirmed = nextNonce;
          }
        }
      }
    } finally {
      this.releaseLock(state);
    }
  }

  /**
   * Reject nonce on failure/drop (no increment)
   * Removes from pending, allows retry with same nonce
   * 
   * @param owner - Wallet address
   * @param nonce - Nonce value to reject
   * @param isOffChain - Whether this is an off-chain operation
   */
  async rejectNonce(owner: string, nonce: number, isOffChain: boolean = false): Promise<void> {
    const normalizedOwner = owner.toLowerCase();
    const state = this.getOrCreateState(normalizedOwner);

    await this.acquireLock(state);
    
    try {
      // Remove from pending list
      const index = state.pending.indexOf(nonce);
      if (index !== -1) {
        state.pending.splice(index, 1);
      }

      // Note: Does NOT increment confirmed nonce
      // This allows retry with the same nonce
      
      // For off-chain: also remove from TTL tracking if exists
      if (isOffChain) {
        const offChainMap = this.offChainNonces.get(normalizedOwner);
        if (offChainMap) {
          offChainMap.delete(nonce);
        }
      }
    } finally {
      this.releaseLock(state);
    }
  }

  /**
   * Get expected next nonce (for client queries)
   * Returns next nonce accounting for pending nonces (gap-tolerant)
   * 
   * @param owner - Wallet address
   * @param reserve - Whether to atomically reserve the nonce
   * @returns Nonce query result
   */
  async getExpectedNextNonce(owner: string, reserve: boolean = false): Promise<NonceQueryResult> {
    const normalizedOwner = owner.toLowerCase();
    
    // Sync with core first
    await this.syncWithCore(normalizedOwner);
    
    const state = this.getOrCreateState(normalizedOwner);
    
    // Calculate next expected nonce
    // If there are pending nonces, next nonce is max(pending) + 1
    // Otherwise, next nonce is confirmed + 1
    const maxPending = state.pending.length > 0 
      ? Math.max(...state.pending) 
      : state.confirmed;
    const nextNonce = Math.max(state.confirmed + 1, maxPending + 1);
    
    // Handle new account case (server returns 0)
    const finalNonce = nextNonce === 0 ? BASE_NONCE : nextNonce;
    
    // Reserve if requested
    if (reserve) {
      try {
        await this.validateNonce(normalizedOwner, finalNonce, false);
      } catch (error: any) {
        // If reservation fails, return nonce anyway (client can retry)
        console.warn(`[NonceManager] Failed to reserve nonce ${finalNonce}:`, error.message);
      }
    }
    
    return {
      next_nonce: finalNonce,
      reserved: reserve,
      timestamp: Date.now(),
    };
  }

  /**
   * Cleanup expired off-chain nonces (TTL cleanup)
   * Should be called periodically
   */
  cleanupExpiredOffChainNonces(): void {
    const now = Date.now();
    
    for (const [owner, nonceMap] of this.offChainNonces.entries()) {
      for (const [nonce, timestamp] of nonceMap.entries()) {
        if (now - timestamp >= OFF_CHAIN_TTL) {
          nonceMap.delete(nonce);
        }
      }
      
      // Remove empty maps
      if (nonceMap.size === 0) {
        this.offChainNonces.delete(owner);
      }
    }
  }

  /**
   * Reset nonce state for an owner (for testing/error recovery)
   */
  resetNonce(owner: string): void {
    const normalizedOwner = owner.toLowerCase();
    this.onChainState.delete(normalizedOwner);
    this.offChainNonces.delete(normalizedOwner);
    
    const timer = this.syncTimers.get(normalizedOwner);
    if (timer) {
      clearInterval(timer);
      this.syncTimers.delete(normalizedOwner);
    }
  }

  /**
   * Get current nonce state (for debugging)
   */
  getState(owner: string): NonceState | undefined {
    const normalizedOwner = owner.toLowerCase();
    return this.onChainState.get(normalizedOwner);
  }
}
